import { useState } from 'react';
import { db } from '../../config/firebase';
import { collection, query, where, getDocs, updateDoc, addDoc, doc, serverTimestamp } from 'firebase/firestore';
import { advanceDealStage, denyDeal, suspendDeal } from '../../services/dealService';
import { sendCompanyNotification, sendInternalNotification } from '../../services/pifService';
import { writeDealAuditLog } from '../../services/auditService';
import { useAuth } from '../../hooks/useAuth';
import Button from '../shared/UI/Button';
import SelectField from '../shared/Forms/SelectField';
import FormField from '../shared/Forms/FormField';
import { FINAL_STAGE_BY_GATE, FINAL_ADVANCE_ACTION_BY_GATE, ADVANCE_COMPANY_NOTIFICATION_BY_GATE } from '../../utils/formatters';

export default function IICApprovalForm({ deal, approvalRecord, approvalSequence, gate = 'pre_screening', onSaved, onDealUpdated }) {
  const { user, userProfile } = useAuth();
  const [form, setForm] = useState({
    decision: '',
    notes: '',
    info_request_details: '',
    suspension_reason: '',
    reactivation_date: '',
    denial_reason: '',
    dd_budget_approved: '',
    dd_mandate_notes: '',
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);

  function set(field, value) {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  }

  function validate() {
    const e = {};
    if (!form.decision) e.decision = 'Please select a decision';
    // RESERVED FOR IC1 — intentionally unreachable. 'approve_all' was removed from the
    // decision options, so form.decision can never hold it and this never runs. Kept
    // deliberately: IC1 will re-host the three-in-one sign-off and needs this validation.
    // Do not "clean up".
    if (form.decision === 'approve_all') {
      const budget = parseFloat(form.dd_budget_approved);
      if (!form.dd_budget_approved || isNaN(budget) || budget <= 0) {
        e.dd_budget_approved = 'Please enter an approved DD budget amount';
      }
    }
    if (form.decision === 'request_additional_information' && !form.info_request_details?.trim()) {
      e.info_request_details = 'Please describe the information required';
    }
    if (form.decision === 'suspend' && !form.suspension_reason?.trim()) {
      e.suspension_reason = 'Please provide a reason for suspension';
    }
    if (form.decision === 'deny' && !form.denial_reason?.trim()) {
      e.denial_reason = 'Please provide a reason for denial';
    }
    return e;
  }

  // Only the approver who requested the information may confirm it has arrived.
  // Clearing information_pending re-opens their (still 'pending') step for a decision.
  async function handleMarkInfoReceived() {
    setSaving(true);
    try {
      const performedByName = userProfile?.full_name || userProfile?.display_name || user?.email || 'Unknown';
      const receivedAt = new Date().toISOString();
      const currentStageOrder = approvalRecord?.stage_order || 1;

      const approvalsSnap = await getDocs(
        query(
          collection(db, 'deals', deal.id, 'iic_approvals'),
          where('stage_order', '==', currentStageOrder),
          where('gate', '==', gate)
        )
      );
      if (!approvalsSnap.empty) {
        await updateDoc(doc(db, 'deals', deal.id, 'iic_approvals', approvalsSnap.docs[0].id), {
          info_requested: false,
          info_received_at: receivedAt,
          info_received_by: user?.uid || null,
        });
      }

      await updateDoc(doc(db, 'deals', deal.id), {
        status: 'active',
        iic_info_received_at: receivedAt,
        iic_info_received_by: user?.uid || null,
        iic_info_received_by_name: performedByName,
      });

      await writeDealAuditLog({
        dealId: deal.id,
        action: 'iic_information_received',
        performedBy: user?.uid,
        performedByName,
        details: { request_notes: deal.iic_info_request_notes || '', stage: deal.current_stage },
        stageAtTime: deal.current_stage,
      });

      onDealUpdated?.();
    } catch (err) {
      setErrors({ _global: err.message });
    } finally {
      setSaving(false);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setSaving(true);
    try {
      const stages = approvalSequence?.stages || [];
      const currentStageOrder = approvalRecord?.stage_order || 1;
      const isLastStage = currentStageOrder >= stages.length;

      // Requesting information is provisional, not a decision: the step stays
      // 'pending' so the approver can return to it once the info arrives.
      const isInfoRequest = form.decision === 'request_additional_information';

      // Update the current IIC approval document
      const approvalsSnap = await getDocs(
        query(
          collection(db, 'deals', deal.id, 'iic_approvals'),
          where('stage_order', '==', currentStageOrder),
          where('gate', '==', gate)
        )
      );

      if (!approvalsSnap.empty) {
        await updateDoc(doc(db, 'deals', deal.id, 'iic_approvals', approvalsSnap.docs[0].id), {
          status: isInfoRequest
            ? 'pending'
            : (form.decision === 'advance' || form.decision === 'approve_all') ? 'approved' : form.decision,
          approver_id: isInfoRequest ? null : user?.uid,
          approved_by_name: isInfoRequest ? null : (userProfile?.full_name || userProfile?.display_name || user?.email || 'Unknown'),
          performed_by_name: userProfile?.full_name || userProfile?.display_name || user?.email || 'Unknown',
          notes: form.notes || null,
          info_request_details: form.info_request_details || null,
          suspension_reason: form.suspension_reason || null,
          dd_budget_approved: form.decision === 'approve_all' ? parseFloat(form.dd_budget_approved) : null,
          dd_mandate_notes: form.decision === 'approve_all' ? (form.dd_mandate_notes || null) : null,
          info_requested: isInfoRequest,
          info_requested_at: isInfoRequest ? new Date().toISOString() : null,
          info_requested_by: isInfoRequest ? (user?.uid || null) : null,
          decided_at: isInfoRequest ? null : serverTimestamp(),
        });
      }

      const performedByName = userProfile?.full_name || userProfile?.display_name || user?.email || 'Unknown';

      // RESERVED FOR IC1 — intentionally unreachable. 'approve_all' was removed from the
      // decision options because it bypassed the sequential approval chain: a single
      // approver could skip CIO/MD and Screening and jump the deal straight to Due
      // Diligence. The code is kept because IC1 will re-host this three-in-one sign-off
      // in its proper place. Do not "clean up" — this is working code we need.
      if (form.decision === 'approve_all') {
        // Three-in-one IIC sign-off: in-principle approval + DD budget
        // approval + DD mandate. Advances the deal to due diligence.
        const ddBudget = parseFloat(form.dd_budget_approved);
        await advanceDealStage({
          dealId: deal.id,
          newStage: 'due_diligence',
          extraUpdates: {
            status: 'active',
            dd_budget_approved: ddBudget,
            dd_mandate_notes: form.dd_mandate_notes || '',
            iic_decision: 'approved',
            advanced_to_dd_at: new Date().toISOString(),
            advanced_to_dd_by: user?.uid,
            advanced_to_dd_by_name: performedByName,
          },
          performedBy: user?.uid,
          performedByName,
          action: 'advanced_to_due_diligence',
        });
        await writeDealAuditLog({
          dealId: deal.id,
          action: 'advanced_to_due_diligence',
          performedBy: user?.uid,
          performedByName,
          details: {
            dd_budget: ddBudget,
            mandate_notes: form.dd_mandate_notes || '',
          },
          stageAtTime: deal.current_stage,
        });

      } else if (form.decision === 'advance') {
        // Clear information_pending status if set
        if (deal.status === 'information_pending') {
          await updateDoc(doc(db, 'deals', deal.id), { status: 'active' });
        }

        if (!isLastStage) {
          const nextStage = stages.find(s => s.stage_order === currentStageOrder + 1);

          // Create next IIC approval document
          await addDoc(collection(db, 'deals', deal.id, 'iic_approvals'), {
            deal_id: deal.id,
            gate,
            stage_order: currentStageOrder + 1,
            approver_role: nextStage?.role || 'cio',
            approver_id: null,
            status: 'pending',
            notes: null,
            requested_at: serverTimestamp(),
            decided_at: null,
          });

          // Notify next approver
          await sendInternalNotification({
            recipientIds: [],
            subject: `IIC Approval Required — ${deal.company_name}`,
            body: `Your IIC approval (stage ${currentStageOrder + 1}) is required for ${deal.company_name}.`,
            dealId: deal.id,
          }).catch(() => {});

        } else {
          // Final stage — advance to the gate's target stage with a gate-aware
          // audit label and company notification. pre_screening -> 'screening'
          // ('iic_approval_complete' / 'advancing_to_screening', unchanged);
          // ic1 -> 'ic1' ('iic_signoff_complete' / 'presenting_at_ic1', the
          // decision to present the deal at IC1 — docs/01:232).
          await advanceDealStage({
            dealId: deal.id,
            newStage: FINAL_STAGE_BY_GATE[gate],
            extraUpdates: { status: 'active' },
            performedBy: user?.uid,
            performedByName,
            action: FINAL_ADVANCE_ACTION_BY_GATE[gate],
          });
          // Notify company
          await sendCompanyNotification({
            dealId: deal.id,
            notificationType: ADVANCE_COMPANY_NOTIFICATION_BY_GATE[gate],
            additionalData: {},
          }).catch(() => {});
        }

      } else if (form.decision === 'deny') {
        await denyDeal({
          dealId: deal.id,
          reason: form.denial_reason,
          performedBy: user?.uid,
          performedByName,
          stageAtTime: 'iic_approval',
        });
        await sendCompanyNotification({
          dealId: deal.id,
          notificationType: 'not_advancing',
          additionalData: {},
        }).catch(() => {});

      } else if (form.decision === 'suspend') {
        const reactivationDate = form.reactivation_date ? new Date(form.reactivation_date) : null;
        await suspendDeal({
          dealId: deal.id,
          reason: form.suspension_reason,
          reactivationDate,
          performedBy: user?.uid,
          performedByName,
          stageAtTime: 'iic_approval',
        });
        await sendCompanyNotification({
          dealId: deal.id,
          notificationType: 'suspended',
          additionalData: {},
        }).catch(() => {});

      } else if (form.decision === 'request_additional_information') {
        // Mark deal as information_pending (stage does not change)
        await updateDoc(doc(db, 'deals', deal.id), {
          status: 'information_pending',
          iic_info_requested_at: new Date().toISOString(),
          iic_info_requested_by: user?.uid,
          iic_info_requested_by_name: performedByName,
          iic_info_request_notes: form.info_request_details || '',
        });
        await writeDealAuditLog({
          dealId: deal.id,
          action: 'iic_information_requested',
          performedBy: user?.uid,
          performedByName,
          details: { notes: form.info_request_details, stage: deal.current_stage },
          stageAtTime: deal.current_stage,
        });
        // Notify analyst to provide info
        if (deal.assigned_analyst_id) {
          await sendInternalNotification({
            recipientIds: [deal.assigned_analyst_id],
            subject: `Additional Information Required — ${deal.company_name}`,
            body: `An IIC approver has requested additional information for ${deal.company_name}.\n\nRequest: ${form.info_request_details}`,
            dealId: deal.id,
          }).catch(() => {});
        }
      }

      setSuccess(true);
      setTimeout(() => onSaved?.(), 2000);
    } catch (err) {
      setErrors({ _global: err.message });
    } finally {
      setSaving(false);
    }
  }

  const stages = approvalSequence?.stages || [];
  const currentStageOrder = approvalRecord?.stage_order || 1;
  const isLastStage = currentStageOrder >= stages.length;

  if (!user) return null;

  const isInfoPending = deal.status === 'information_pending';
  // Scoped to THIS step's doc, so a request on an earlier step does not banner the next approver.
  const stepHadInfoRequest = !!approvalRecord?.info_requested_at;
  const isRequester = !!deal.iic_info_requested_by && user?.uid === deal.iic_info_requested_by;

  const infoBanner = (isInfoPending || stepHadInfoRequest) ? (
    <div className="alert alert-warning" style={{ marginBottom: 16 }}>
      ⏳ Additional information was requested on{' '}
      {deal.iic_info_requested_at
        ? new Date(deal.iic_info_requested_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
        : 'an earlier date'}{' '}
      by {deal.iic_info_requested_by_name || 'an IIC approver'}.
      {deal.iic_info_request_notes && ` Request: "${deal.iic_info_request_notes}"`}
      {' '}{isInfoPending
        ? 'Voting is paused until the information is marked received.'
        : 'The information was marked received — you can approve or decline below.'}
    </div>
  ) : null;

  // Voting is paused while information is outstanding: show the paused state, not
  // the decision form. Prior approvals on earlier steps are untouched and still stand.
  if (isInfoPending) {
    return (
      <div>
        {errors._global && <div className="alert alert-error">{errors._global}</div>}
        {infoBanner}
        <div className="card">
          <div className="card-header">
            <h2>IIC Approval — Stage {currentStageOrder} of {stages.length} · Paused</h2>
          </div>
          <div className="card-body">
            {isRequester ? (
              <>
                <p style={{ fontSize: 13, marginBottom: 12, color: 'var(--color-text-secondary)' }}>
                  You requested this information. Once it has been received, mark it
                  received to re-open your decision — your place in the approval
                  sequence is held and earlier approvals still stand.
                </p>
                <Button variant="primary" onClick={handleMarkInfoReceived} loading={saving}>
                  Mark Information Received
                </Button>
              </>
            ) : (
              <p style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
                Voting is paused until {deal.iic_info_requested_by_name || 'the requesting approver'}{' '}
                confirms the requested information has been received.
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit}>
      {errors._global && <div className="alert alert-error">{errors._global}</div>}
      {success && <div className="alert alert-success">Decision recorded!</div>}

      {infoBanner}

      <div className="card mb-4">
        <div className="card-header">
          <h2>IIC Approval — Stage {currentStageOrder} of {stages.length}</h2>
        </div>
        <div className="card-body">
          <div className="form-grid">
            <FormField label="Notes" className="form-full">
              <textarea className="form-textarea" value={form.notes}
                onChange={e => set('notes', e.target.value)} rows={4}
                placeholder="Optional notes on your decision…" />
            </FormField>

            <SelectField
              label="Decision"
              required
              error={errors.decision}
              value={form.decision}
              onChange={e => set('decision', e.target.value)}
              options={[
                { value: 'advance', label: isLastStage ? 'Approve — Advance to Screening' : 'Advance to Next Approver' },
                { value: 'deny', label: 'Deny' },
                ...(!isInfoPending ? [{ value: 'request_additional_information', label: 'Request Additional Information' }] : []),
                { value: 'suspend', label: 'Suspend' },
              ]}
            />

            {/* RESERVED FOR IC1 — intentionally unreachable. 'approve_all' is no longer
                an option above, so this block never renders. Kept for IC1 to re-host.
                Do not "clean up". */}
            {form.decision === 'approve_all' && (
              <>
                <div className="form-full" style={{
                  padding: '10px 14px', background: 'var(--color-success-bg)',
                  borderRadius: 'var(--radius)', fontSize: 13, color: 'var(--color-success)',
                }}>
                  ✅ This records all three Investment Committee approvals together:
                  in-principle approval to proceed, DD budget approval, and the DD mandate.
                  On submission the deal advances to Due Diligence.
                </div>
                <FormField
                  label="Approved DD Budget"
                  required
                  error={errors.dd_budget_approved}
                  hint={`Currency: ${deal.requested_currency || deal.currency || 'USD'}`}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--color-text-secondary)' }}>
                      {deal.requested_currency || deal.currency || 'USD'}
                    </span>
                    <input
                      type="number"
                      min="0"
                      step="any"
                      className={`form-input ${errors.dd_budget_approved ? 'error' : ''}`}
                      value={form.dd_budget_approved}
                      onChange={e => set('dd_budget_approved', e.target.value)}
                      placeholder="Enter approved budget amount"
                    />
                  </div>
                </FormField>
                <FormField label="DD Mandate Notes" className="form-full" hint="Key areas to focus on during due diligence">
                  <textarea
                    className="form-textarea"
                    value={form.dd_mandate_notes}
                    onChange={e => set('dd_mandate_notes', e.target.value)}
                    rows={4}
                    placeholder="e.g. Prioritise financial audit, governance review, market validation…"
                  />
                </FormField>
              </>
            )}

            {form.decision === 'request_additional_information' && (
              <FormField label="Information Required" required error={errors.info_request_details} className="form-full">
                <textarea className={`form-textarea ${errors.info_request_details ? 'error' : ''}`}
                  value={form.info_request_details}
                  onChange={e => set('info_request_details', e.target.value)} rows={3} />
              </FormField>
            )}

            {form.decision === 'suspend' && (
              <>
                <FormField label="Suspension Reason" required error={errors.suspension_reason} className="form-full">
                  <textarea className={`form-textarea ${errors.suspension_reason ? 'error' : ''}`}
                    value={form.suspension_reason}
                    onChange={e => set('suspension_reason', e.target.value)} rows={3} />
                </FormField>
                <FormField label="Reactivation Date" hint="Optional — leave blank for manual reactivation">
                  <input type="date" className="form-input" value={form.reactivation_date}
                    onChange={e => set('reactivation_date', e.target.value)}
                    min={new Date().toISOString().split('T')[0]} />
                </FormField>
              </>
            )}

            {form.decision === 'deny' && (
              <FormField label="Denial Reason" required error={errors.denial_reason} className="form-full">
                <textarea className={`form-textarea ${errors.denial_reason ? 'error' : ''}`}
                  value={form.denial_reason}
                  onChange={e => set('denial_reason', e.target.value)} rows={3} />
              </FormField>
            )}
          </div>
        </div>
      </div>

      <div className="flex-between">
        <div />
        <Button variant="primary" type="submit" loading={saving}>Record Decision</Button>
      </div>
    </form>
  );
}
